import {Alert, Button, Card, Modal, Typography} from 'antd';
import {
  MessageChannel,
  SendSubscriptionMessageInput,
  SubscriptionEmailType,
  SubscriptionFieldsFragment,
  usePreviewSubscriptionEmailQuery,
  useSendSubscriptionStatusMutation
} from 'api/generated';
import {PhoneNumberInput} from 'components';
import {HtmlPreview} from 'components/HtmlPreview';
import {Formik, FormikProps} from 'formik';
import {Form, FormItem, Input, Select} from 'formik-antd';
import {FormikHelpers} from 'formik/dist/types';
import {useHostedPaymentDomain} from 'hooks/useHostedPaymentDomain';
import ISO6391 from 'iso-639-1';
import {omitEmpty} from 'lib/omitEmpty';
import {validatePhone} from 'lib/validation';
import React, {useMemo, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {config} from 'stage.config';
import styled from 'styled-components';
import * as yup from 'yup';
import {ScrollToError} from '../../../components/Form/ScrollToError';
import {useCountries} from '../../../hooks/useCountries';

type Props = {
  subscription: SubscriptionFieldsFragment;
};

type FormValues = SendSubscriptionMessageInput;

const {Text} = Typography;
const {Option} = Select;

const Preview = styled(HtmlPreview)`
  width: 100%;
  border: 1px solid #d9d9d9;
  border-radius: 2px;
`;

type ChannelOption = {
  label: string;
  value: MessageChannel;
};

export const SendSubscriptionStatus: React.FC<Props> = ({subscription}) => {
  const location = useLocation();
  const [sendSubscriptionStatus] = useSendSubscriptionStatusMutation();
  const hostedSubscriptionDomain = useHostedPaymentDomain(!subscription, subscription.accountId);
  const [isOpen, setOpen] = useState((location.state as any)?.action === 'sendSubscriptionStatus');
  const [language, setLanguage] = useState('en');
  const {currentCountry} = useCountries();
  const formikRef = React.useRef<FormikProps<FormValues>>(null);
  const channelOptions: ChannelOption[] = [
    {
      label: 'Email',
      value: MessageChannel.EMAIL
    },
    {
      label: 'WhatsApp',
      value: MessageChannel.WHATSAPP
    },
    {
      label: 'SMS',
      value: MessageChannel.SMS
    }
  ];

  const {data} = usePreviewSubscriptionEmailQuery({
    variables: {
      subscriptionId: subscription.id,
      accountId: subscription.accountId,
      emailType: SubscriptionEmailType.SUBSCRIPTION_LINK,
      language
    }
  });

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        channel: yup.string().required(),
        customerEmail: yup.string().when('channel', {
          is: MessageChannel.EMAIL,
          otherwise: yup.string().optional(),
          then: yup.string().email('Invalid email format').required('Email is required')
        }),
        customerPhone: yup.string().when('channel', {
          is: MessageChannel.EMAIL,
          then: yup.string().optional(),
          otherwise: yup
            .string()
            .test('phone', 'Invalid phone format', validatePhone)
            .required('Phone is required')
        })
      }),
    []
  );

  const initialValues: FormValues = omitEmpty({
    accountId: subscription?.accountId,
    subscriptionId: subscription?.id,
    customerEmail: subscription.customer?.email,
    customerPhone: subscription.customer?.phone,
    channel: MessageChannel.EMAIL,
    language
  });

  const handleSubmit = async (input: FormValues, {setSubmitting}: FormikHelpers<FormValues>) => {
    try {
      await sendSubscriptionStatus({variables: {input}});
      setOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  const actionTitle = 'Send status';

  return (
    <>
      <Button onClick={() => setOpen(true)}>{actionTitle}</Button>
      <Formik
        innerRef={formikRef}
        validationSchema={validationSchema}
        initialValues={initialValues}
        onSubmit={handleSubmit}>
        {({submitForm, isSubmitting, resetForm, values}) => {
          const channel = values.channel as MessageChannel;
          return (
            <Modal
              title={actionTitle}
              okText={actionTitle}
              cancelText={'Cancel'}
              open={isOpen}
              width={600}
              confirmLoading={isSubmitting}
              afterClose={() => resetForm({values})}
              onOk={submitForm}
              onCancel={() => {
                setOpen(false);
              }}>
              <Form layout="vertical">
                {channelOptions.length && (
                  <FormItem name="channel" label={'Delivery channel'}>
                    <Select name="channel" options={channelOptions} />
                  </FormItem>
                )}
                {channel === MessageChannel.EMAIL ? (
                  <FormItem name="customerEmail" label={'Send to'}>
                    <Input name="customerEmail" placeholder="email@example.com" />
                  </FormItem>
                ) : (
                  <FormItem name="customerPhone" label={'Send to'}>
                    <PhoneNumberInput name="customerPhone" countryCode={currentCountry?.code} />
                  </FormItem>
                )}
                <FormItem name="language" label={'Customer language'}>
                  <Select name="language" onChange={(value) => setLanguage(value)}>
                    {config.supportedLanguages.map((code) => (
                      <Option key={code} value={code}>
                        {ISO6391.getNativeName(code)}
                      </Option>
                    ))}
                  </Select>
                </FormItem>
                {channel === MessageChannel.EMAIL && (
                  <FormItem
                    name="preview"
                    label={<span>Email preview</span>}
                    style={{marginBottom: 0}}>
                    {data ? (
                      <Preview html={data?.previewSubscriptionEmail || ''} />
                    ) : (
                      <Card loading style={{height: 400}} />
                    )}
                  </FormItem>
                )}
                <input type="submit" value="submit" hidden />
                {channel === MessageChannel.WHATSAPP && (
                  <Alert
                    type="info"
                    description={
                      <>
                        If the phone number is not registered in WhatsApp, the customer will receive
                        a link to pay via SMS.
                      </>
                    }
                  />
                )}
                <ScrollToError parent={document.querySelector('.ant-modal-wrap') as HTMLElement} />
              </Form>
            </Modal>
          );
        }}
      </Formik>
    </>
  );
};
